import { Input, Text } from "@clipboard-health/ui-components";
import { Autocomplete } from "@react-google-maps/api";
import { type Address, type Location, parseAddressComponents } from "@src/appV2/lib/GoogleMaps";
import { useEffect, useRef, useState } from "react";

import { GoogleMapErrorBoundary } from "../GeoLocation/GoogleMapErrorBoundary";

interface AddressAutocompleteProps {
  addressInputValue: string;
  onAddressInputChange: (value: string) => void;
  onPlaceChanged: ({ address, location }: { address: Address; location: Location }) => void;
  errorMessage?: string;
  shouldFocusOnRender?: boolean;
  disabled?: boolean;
}

export function AddressAutocomplete(props: AddressAutocompleteProps) {
  const {
    onPlaceChanged,
    addressInputValue,
    onAddressInputChange,
    errorMessage,
    shouldFocusOnRender,
    disabled,
  } = props;

  const [autocomplete, setAutocomplete] = useState<google.maps.places.Autocomplete | undefined>(
    undefined
  );
  const [hasFocused, setHasFocused] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (shouldFocusOnRender && inputRef.current && !hasFocused) {
      inputRef.current.focus();
      setHasFocused(true);
    }
  }, [shouldFocusOnRender, inputRef, hasFocused]);

  function updateAddressAndLocation(
    place: google.maps.GeocoderResult | google.maps.places.PlaceResult,
    location: Location
  ) {
    const newAddress = {
      ...parseAddressComponents(place.address_components ?? []),
      formatted: place.formatted_address,
    };

    onPlaceChanged({ address: newAddress, location });
  }

  return (
    <GoogleMapErrorBoundary>
      <Autocomplete
        onLoad={setAutocomplete}
        onPlaceChanged={() => {
          const place = autocomplete?.getPlace();
          if (place?.geometry?.location) {
            const newLocation: Location = {
              lat: place.geometry.location.lat(),
              lng: place.geometry.location.lng(),
            };
            updateAddressAndLocation(place, newLocation);
          }
        }}
      >
        <>
          <Input
            fullWidth
            inputRef={inputRef}
            value={addressInputValue}
            size="large"
            error={Boolean(errorMessage)}
            disabled={disabled}
            onChange={(event) => {
              onAddressInputChange(event.target.value);
            }}
          />

          <Text variant="caption" color={(theme) => theme.palette.intent?.error.text} pl={2} pt={2}>
            {errorMessage}
          </Text>
        </>
      </Autocomplete>
    </GoogleMapErrorBoundary>
  );
}
